import { useEffect, useRef, useCallback } from 'react'
import { useInterviewStore } from '@/store/interviewStore'
import { buildApiUrl, getApiHeaders } from '@/config/api'

interface UMLAutosaveOptions {
  intervalMs?: number
  classes: any[]
  relationships: any[]
}

export function useUMLAutoSave({ 
  intervalMs = 5000, 
  classes = [], 
  relationships = []
}: UMLAutosaveOptions) {
  const store = useInterviewStore()
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const lastSavedRef = useRef<string>('')
  const isSavingRef = useRef<boolean>(false)

  const saveToAPI = useCallback(async (data: any) => {
    if (isSavingRef.current) return
    
    const { interviewId } = store
    if (!interviewId) {
      console.warn('Cannot save UML design: missing interviewId')
      return
    }

    try {
      isSavingRef.current = true
      
      const requestBody = {
        interviewId,
        content: '', // We'll use JSON data instead of text content
        save_type: 'auto',
        classes_data: JSON.stringify(data.classes || []),
        relationships_data: JSON.stringify(data.relationships || []),
        diagram_type: 'class' // Default to class diagram
      }

      console.log('🔄 Auto-saving UML design...')

      const response = await fetch(buildApiUrl('interview-responses/public/save/uml_design'), {
        method: 'POST',
        headers: {
          ...getApiHeaders(false), // Public endpoint, no auth required
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      })

      if (!response.ok) {
        const error = await response.json()
        console.error('❌ UML design autosave failed:', error)
        return
      }

      // Mark as clean in store
      store.markStageClean('uml')
      console.log('✅ Auto-saved UML design')

    } catch (error) {
      console.error('❌ UML design autosave error:', error)
    } finally {
      isSavingRef.current = false
    }
  }, [store])

  const performSave = useCallback(() => {
    // Create current data snapshot
    const currentData = {
      classes,
      relationships
    }

    // Check if data has changed
    const dataString = JSON.stringify(currentData)
    const hasChanged = dataString !== lastSavedRef.current

    // Check if there's meaningful content
    const hasContent = Boolean(
      classes.length > 0 || 
      relationships.length > 0
    )
    
    console.log('🔍 UML design checking changes:')
    console.log(`   - hasChanged: ${hasChanged}`)
    console.log(`   - hasContent: ${hasContent}`)
    console.log(`   - classes: ${classes.length}`)
    console.log(`   - relationships: ${relationships.length}`)
    
    // Only save if data has actually changed AND has meaningful content
    if (hasChanged && hasContent) {
      console.log('📝 UML design meaningful changes detected - triggering auto-save')
      saveToAPI(currentData)
      lastSavedRef.current = dataString
    } else {
      console.log('⏭️ UML design no meaningful changes - skipping API call')
    }
  }, [classes, relationships, saveToAPI])

  // Set up periodic auto-save
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    console.log('🟢 useUMLAutoSave: Starting periodic save')
    intervalRef.current = setInterval(() => {
      console.log('⏰ useUMLAutoSave: interval triggered')
      performSave()
    }, intervalMs)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      console.log('🟢 useUMLAutoSave: Periodic save stopped')
    }
  }, [performSave, intervalMs])

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        console.log('🔄 Page hidden, triggering immediate UML design save...')
        performSave()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [performSave])

  // Get current stage data for dirty state
  const stageData = store.getUMLData()

  return {
    isDirty: Boolean(stageData.isDirty || classes.length > 0 || relationships.length > 0),
    lastSaved: stageData.lastSaved,
    save: performSave
  }
}
